import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Select, TextField, Typography } from '@glplus/glpweb-dsl';
import { Panel } from './Panel';
import { runnerApi, RunnerApiError, type MyLimits } from '../lib/runnerApiClient';
import { ORANGE } from '../styles/colors';

const NAVY = '#343F5B';
const GREEN = '#2E9E3A';

/**
 * "Your limits" — the Runner's ceiling on weekly deliveries and households,
 * where it comes from, and the way to ask for more (Figma §16 row 5, Rod
 * 2026-09-10/11).
 *
 * 🔒 The numbers are the server's. Nothing here computes a tier; the panel
 * shows `stage`, `limits` and `source` exactly as runner-api serves them, so a
 * person's override reads the same as an earned tier — just labelled.
 * 🔒 "Ask for a higher limit" is a REQUEST, read by a person. The copy says so
 * and never promises an outcome or a time beyond "usually two business days".
 */
export function LimitsPanel({ onLoaded }: { onLoaded?: (limits: MyLimits) => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [limits, setLimits] = useState<MyLimits | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [kind, setKind] = useState<'deliveries' | 'households' | 'both'>('deliveries');
  const [deliveries, setDeliveries] = useState('');
  const [households, setHouseholds] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const l = await runnerApi.myLimits();
      setLimits(l);
      onLoaded?.(l);
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('schedule.loadFailed'));
    }
  }, [onLoaded, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error" data-testid="limits-load-error">{error}</Alert>;
  if (!limits) return null;

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  const overridden = limits.source.deliveries === 'override' || limits.source.households === 'override';
  const dLimit = limits.limits.deliveries;
  const wantsDeliveries = kind !== 'households';
  const wantsHouseholds = kind !== 'deliveries';
  const nD = Number(deliveries), nH = Number(households);
  const deliveriesOk = !wantsDeliveries || (Number.isInteger(nD) && nD > 0 && (dLimit === null ? false : nD > dLimit));
  const householdsOk = !wantsHouseholds || (Number.isInteger(nH) && nH > limits.limits.households);
  const canSend = deliveriesOk && householdsOk && !busy;

  async function send() {
    setBusy(true);
    setFormError(null);
    try {
      await runnerApi.requestLimitRaise({
        kind,
        ...(wantsDeliveries ? { requestedDeliveries: nD } : {}),
        ...(wantsHouseholds ? { requestedHouseholds: nH } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setSent(true);
      setAsking(false);
      await load();
    } catch (e) {
      // runner-api's 409/400 messages are written for the Runner — show them as-is.
      setFormError(e instanceof RunnerApiError ? e.message : t('schedule.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={t('limits.title')} accent={overridden ? ORANGE : NAVY} dataTestId="limits-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip data-testid="limits-stage" label={overridden ? t('limits.adjusted') : t(`limits.stage.${limits.stage}`)} size="small" backgroundColor={overridden ? '#ffedd5' : '#e8ebf2'} textColor={overridden ? '#9a3412' : NAVY} fontWeight={700} fontSize={11} />
        {limits.overrideExpiresAt && (
          <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>→ {fmtDate(limits.overrideExpiresAt)}</Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <Meter testId="limits-meter-deliveries" label={t('limits.deliveries')} used={limits.usage.weeklyCapacity} limit={dLimit} noCeiling={t('limits.noCeiling')} of={(used, limit) => t('limits.of', { used, limit })} />
        <Meter testId="limits-meter-households" label={t('limits.households')} used={limits.usage.activeHouseholds} limit={limits.limits.households} noCeiling={t('limits.noCeiling')} of={(used, limit) => t('limits.of', { used, limit })} />
      </Box>

      <Typography sx={{ color: '#4b5563', fontSize: '13px', mt: 1.5 }}>
        {overridden ? t('limits.adjustedBody') : t('limits.rises')}
      </Typography>
      {overridden && limits.overrideReason && (
        <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mt: 0.5, fontStyle: 'italic' }}>“{limits.overrideReason}”</Typography>
      )}

      {sent && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="success" data-testid="limits-sent">{t('limits.form.sent')}</Alert>
        </Box>
      )}

      {limits.pendingRequest ? (
        <Box data-testid="limits-pending" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
          <Typography sx={{ fontWeight: 700, color: '#9a3412', fontSize: '13px' }}>{t('limits.ask')} · {fmtDate(limits.pendingRequest.createdAt)}</Typography>
          <Typography sx={{ color: '#4b5563', fontSize: '13px' }}>{t('limits.pending')}</Typography>
        </Box>
      ) : (
        <>
          {limits.lastDecision && (
            <Box data-testid="limits-last-decision" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontWeight: 700, color: limits.lastDecision.status === 'approved' ? GREEN : '#5f6b7a', fontSize: '13px' }}>
                {limits.lastDecision.status === 'approved' ? t('limits.approved') : t('limits.declined')}
                {limits.lastDecision.decidedAt ? ` · ${fmtDate(limits.lastDecision.decidedAt)}` : ''}
              </Typography>
              {limits.lastDecision.decisionReason && <Typography sx={{ color: '#4b5563', fontSize: '13px' }}>{limits.lastDecision.decisionReason}</Typography>}
              {limits.canRequestAfter && <Typography data-testid="limits-ask-again" sx={{ color: '#5f6b7a', fontSize: '12px', mt: 0.5 }}>{t('limits.askAgain', { date: fmtDate(limits.canRequestAfter) })}</Typography>}
            </Box>
          )}
          {!limits.canRequestAfter && !asking && (
            <Box sx={{ mt: 1.5 }}>
              <Button ariaLabel={t('limits.ask')} dataTestId="limits-ask" buttonText={t('limits.ask')} variant="outlined" size="small" onClick={() => { setSent(false); setAsking(true); }} />
            </Box>
          )}
          {asking && (
            <Box data-testid="limits-form" sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef2f6' }}>
              <Typography sx={{ fontWeight: 800, color: '#1f2937', fontSize: '15px' }}>{t('limits.form.title')}</Typography>
              <Typography sx={{ color: '#4b5563', fontSize: '13px', mb: 1.5 }}>{t('limits.form.body')}</Typography>
              {formError && (
                <Box sx={{ mb: 1.5 }}>
                  <Alert severity="error" data-testid="limits-form-error">{formError}</Alert>
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Select
                    ariaLabel={t('limits.form.which')}
                    data-testid="limits-form-kind"
                    label={t('limits.form.which')}
                    native
                    size="small"
                    value={kind}
                    onChange={(_e, v) => setKind(v as typeof kind)}
                    options={[
                      ...(dLimit !== null ? [{ value: 'deliveries', label: t('limits.form.deliveries') }] : []),
                      { value: 'households', label: t('limits.form.households') },
                      ...(dLimit !== null ? [{ value: 'both', label: t('limits.form.both') }] : []),
                    ]}
                    sx={{ width: '100%' }}
                  />
                </Box>
                {wantsDeliveries && dLimit !== null && (
                  <Box>
                    <TextField label={`${t('limits.form.deliveries')} — ${t('limits.form.howMany')}`} ariaLabel={t('limits.form.deliveries')} dataTestId="limits-form-deliveries" size="small" type="number" fullWidth value={deliveries} disabled={busy} onChange={(e) => setDeliveries(e.target.value)} placeholder={String(dLimit + 5)} />
                  </Box>
                )}
                {wantsHouseholds && (
                  <Box>
                    <TextField label={`${t('limits.form.households')} — ${t('limits.form.howMany')}`} ariaLabel={t('limits.form.households')} dataTestId="limits-form-households" size="small" type="number" fullWidth value={households} disabled={busy} onChange={(e) => setHouseholds(e.target.value)} placeholder={String(limits.limits.households + 2)} />
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                <TextField label={t('limits.form.note')} ariaLabel={t('limits.form.note')} dataTestId="limits-form-note" size="small" fullWidth multiline rows={3} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
              </Box>
              {((wantsDeliveries && deliveries && !deliveriesOk) || (wantsHouseholds && households && !householdsOk)) && (
                <Typography data-testid="limits-form-not-above" sx={{ color: '#b42318', fontSize: '12px', mt: 0.5 }}>{t('limits.form.notAbove')}</Typography>
              )}
              <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1 }}>{t('limits.form.rule')}</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                <Button ariaLabel={t('limits.form.send')} dataTestId="limits-form-send" buttonText={t('limits.form.send')} variant="contained" size="small" onClick={() => void send()} disabled={!canSend} />
                <Button ariaLabel={t('schedule.cancel')} dataTestId="limits-form-cancel" buttonText={t('schedule.cancel')} variant="text" size="small" onClick={() => { setAsking(false); setFormError(null); }} disabled={busy} />
              </Box>
            </Box>
          )}
        </>
      )}
    </Panel>
  );
}

function Meter({ label, used, limit, noCeiling, of, testId }: { label: string; used: number; limit: number | null; noCeiling: string; of: (used: number, limit: number) => string; testId?: string }) {
  const pct = limit === null || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const full = limit !== null && used >= limit;
  return (
    <Box data-testid={testId} data-used={used} data-limit={limit ?? ''} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e5e7eb' }}>
      <Typography sx={{ color: '#5f6b7a', fontSize: '12px', fontWeight: 700, letterSpacing: 0.4 }}>{label.toUpperCase()}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '22px', color: full ? '#b42318' : '#1f2937', lineHeight: 1.2, mt: 0.25 }}>
        {limit === null ? `${used} · ${noCeiling}` : of(used, limit)}
      </Typography>
      {limit !== null && (
        <Box sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: '#eef2f6', overflow: 'hidden' }} aria-hidden>
          <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: full ? '#D93025' : NAVY }} />
        </Box>
      )}
    </Box>
  );
}
